import React, { useState, useEffect, useCallback } from 'react'
import { Mail, ArrowRight, ArrowLeft, CheckCircle, Lock, Key, Smartphone } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

type LoginStep =
  | 'EMAIL_INPUT'
  | 'METHOD_SELECTION'
  | 'PASSWORD_INPUT'
  | 'SMS_OTP_INPUT'
  | 'MAGIC_LINK_SENT'
  | 'RESET_PASSWORD'
  | 'RESET_EMAIL_SENT'

const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(to bottom right, #f1f5f9, #dbeafe, #e0e7ff)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px'
  }}>
    <div style={{ width: '100%', maxWidth: '512px' }}>
      {children}
    </div>
  </div>
)

const CardContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(8px)',
    borderRadius: '24px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    overflow: 'hidden'
  }}>
    {children}
  </div>
)

const GradientHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle: string }> = ({ icon, title, subtitle }) => (
  <div style={{
    background: 'linear-gradient(to right, #2563eb, #4f46e5)',
    padding: '32px',
    textAlign: 'center'
  }}>
    <div style={{
      margin: '0 auto 16px',
      height: '64px',
      width: '64px',
      background: 'rgba(255, 255, 255, 0.2)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {icon}
    </div>
    <h1 style={{
      fontSize: '30px',
      fontWeight: 'bold',
      color: 'white',
      marginBottom: '8px',
      fontFamily: 'Lexend Deca, sans-serif'
    }}>
      {title}
    </h1>
    <p style={{
      color: '#bfdbfe',
      fontWeight: '500',
      fontFamily: 'Lexend Deca, sans-serif'
    }}>
      {subtitle}
    </p>
  </div>
)

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
  <div style={{
    borderRadius: '12px',
    padding: '16px',
    background: '#fef2f2',
    border: '1px solid #fecaca'
  }}>
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <div style={{ flexShrink: 0 }}>
        <div style={{ height: '20px', width: '20px', color: '#f87171' }}>
          <svg viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
      <div style={{ marginLeft: '12px' }}>
        <p style={{
          fontSize: '14px',
          fontWeight: '500',
          color: '#dc2626',
          fontFamily: 'Lexend Deca, sans-serif'
        }}>{message}</p>
      </div>
    </div>
  </div>
)

const PrimaryButton: React.FC<{
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  children: React.ReactNode;
}> = ({ onClick, type = 'button', disabled, loading, loadingText, children }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled || loading}
    style={{
      width: '100%',
      padding: '16px 24px',
      background: 'linear-gradient(to right, #2563eb, #4f46e5)',
      color: 'white',
      fontWeight: '600',
      borderRadius: '12px',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      border: 'none',
      cursor: (disabled || loading) ? 'not-allowed' : 'pointer',
      fontSize: '16px',
      fontFamily: 'Lexend Deca, sans-serif',
      transition: 'all 0.2s',
      opacity: (disabled || loading) ? 0.5 : 1
    }}
    onMouseEnter={(e) => {
      if (!disabled && !loading) {
        const target = e.currentTarget;
        target.style.background = 'linear-gradient(to right, #1d4ed8, #4338ca)';
        target.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
      }
    }}
    onMouseLeave={(e) => {
      if (!disabled && !loading) {
        const target = e.currentTarget;
        target.style.background = 'linear-gradient(to right, #2563eb, #4f46e5)';
        target.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
      }
    }}
  >
    {loading ? (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{
          animation: 'spin 1s linear infinite',
          borderRadius: '50%',
          height: '20px',
          width: '20px',
          border: '2px solid white',
          borderBottomColor: 'transparent',
          marginRight: '8px'
        }}></div>
        {loadingText || 'Loading...'}
      </div>
    ) : (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {children}
      </div>
    )}
  </button>
)

const BackButton: React.FC<{ onClick: () => void; label?: string }> = ({ onClick, label = 'Back' }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      background: 'transparent',
      border: 'none',
      color: '#6b7280',
      fontSize: '14px',
      fontFamily: 'Lexend Deca, sans-serif',
      cursor: 'pointer',
      padding: '8px 0',
      transition: 'color 0.2s'
    }}
    onMouseEnter={(e) => { e.currentTarget.style.color = '#374151' }}
    onMouseLeave={(e) => { e.currentTarget.style.color = '#6b7280' }}
  >
    <ArrowLeft style={{ height: '16px', width: '16px', marginRight: '4px' }} />
    {label}
  </button>
)

const FooterHelp = () => (
  <div style={{ textAlign: 'center', marginTop: '24px' }}>
    <p style={{
      fontSize: '14px',
      color: '#6b7280',
      fontFamily: 'Lexend Deca, sans-serif',
      marginBottom: '12px'
    }}>
      Need help? Contact admin@solharbor.com
    </p>
    <a
      href="https://www.solharbor.com/"
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '14px',
        color: '#2563eb',
        textDecoration: 'none',
        fontFamily: 'Lexend Deca, sans-serif',
        fontWeight: '500',
        transition: 'color 0.2s'
      }}
      onMouseEnter={(e) => { (e.target as HTMLAnchorElement).style.color = '#1d4ed8' }}
      onMouseLeave={(e) => { (e.target as HTMLAnchorElement).style.color = '#2563eb' }}
    >
      ← Back to main site
    </a>
  </div>
)

const MethodButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  subtitle?: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ icon, label, subtitle, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      background: 'white',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s',
      textAlign: 'left',
      gap: '16px'
    }}
    onMouseEnter={(e) => {
      if (!disabled) {
        e.currentTarget.style.borderColor = '#3b82f6';
        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
        e.currentTarget.style.background = '#f8fafc';
      }
    }}
    onMouseLeave={(e) => {
      if (!disabled) {
        e.currentTarget.style.borderColor = '#e5e7eb';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.background = 'white';
      }
    }}
  >
    <div style={{
      height: '48px',
      width: '48px',
      background: '#eff6ff',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0
    }}>
      {icon}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
        fontFamily: 'Lexend Deca, sans-serif'
      }}>
        {label}
      </div>
      {subtitle && (
        <div style={{
          fontSize: '13px',
          color: '#6b7280',
          fontFamily: 'Lexend Deca, sans-serif',
          marginTop: '2px'
        }}>
          {subtitle}
        </div>
      )}
    </div>
    <ArrowRight style={{ height: '20px', width: '20px', color: '#9ca3af', flexShrink: 0 }} />
  </button>
)

export const LoginPage: React.FC = () => {
  const [step, setStep] = useState<LoginStep>('EMAIL_INPUT')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [hasPhone, setHasPhone] = useState(false)
  const [hasPassword, setHasPassword] = useState(false)
  const [maskedPhone, setMaskedPhone] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const [isSetupPassword, setIsSetupPassword] = useState(false)

  const {
    signInWithMagicLink,
    signInWithPassword,
    resetPassword,
    checkAuthMethods,
    sendSmsOtp,
    verifySmsOtp
  } = useAuth()

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const result = await checkAuthMethods(email)
      if (!result.exists) {
        setError('Unrecognised email address')
        setIsLoading(false)
        return
      }
      setHasPhone(result.hasPhone)
      setHasPassword(result.hasPassword)
      setMaskedPhone(result.maskedPhone || '')

      // If user has no password and no phone, send magic link directly
      if (!result.hasPassword && !result.hasPhone) {
        setIsLoading(true)
        const mlResult = await signInWithMagicLink(email)
        if (mlResult.error) {
          setError(mlResult.error.message)
          setIsLoading(false)
        } else {
          setStep('MAGIC_LINK_SENT')
          setIsLoading(false)
        }
        return
      }

      setStep('METHOD_SELECTION')
    } catch (err: any) {
      setError(err.message || 'Something went wrong')
    }
    setIsLoading(false)
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    const result = await signInWithPassword(email, password)
    if (result.error) {
      setError(result.error.message)
    }
    setIsLoading(false)
  }

  const handleSendSmsOtp = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const result = await sendSmsOtp(email)
      setMaskedPhone(result.maskedPhone)
      setResendCooldown(60)
    } catch (err: any) {
      setError(err.message || 'Failed to send code')
    }
    setIsLoading(false)
  }, [email, sendSmsOtp])

  const handleSelectSmsOtp = async () => {
    setStep('SMS_OTP_INPUT')
    await handleSendSmsOtp()
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      await verifySmsOtp(email, otpCode)
      // Session created — onAuthStateChange will redirect
    } catch (err: any) {
      setError(err.message || 'Invalid code')
    }
    setIsLoading(false)
  }

  const handleSetupPassword = async () => {
    setIsLoading(true)
    setError('')

    const result = await resetPassword(email)
    if (result.error) {
      setError(result.error.message)
      setIsLoading(false)
    } else {
      setIsSetupPassword(true)
      setStep('RESET_EMAIL_SENT')
      setIsLoading(false)
    }
  }

  const handleMagicLink = async () => {
    setIsLoading(true)
    setError('')

    const result = await signInWithMagicLink(email)
    if (result.error) {
      setError(result.error.message)
      setIsLoading(false)
    } else {
      setStep('MAGIC_LINK_SENT')
      setIsLoading(false)
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    const result = await resetPassword(email)
    if (result.error) {
      setError(result.error.message)
      setIsLoading(false)
    } else {
      setStep('RESET_EMAIL_SENT')
      setIsLoading(false)
    }
  }

  const goBack = (toStep: LoginStep) => {
    setError('')
    setPassword('')
    setOtpCode('')
    setIsSetupPassword(false)
    setStep(toStep)
  }

  // ─── EMAIL INPUT STEP ─────────────────────────────────
  if (step === 'EMAIL_INPUT') {
    return (
      <PageWrapper>
        <CardContainer>
          <GradientHeader
            icon={<img src="/logo.png" alt="Solharbor Logo" style={{ height: '40px', width: '40px', objectFit: 'contain' }} />}
            title="Solharbor"
            subtitle="Client Portal"
          />
          <div style={{ padding: '32px' }}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '8px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Welcome back
              </h2>
              <p style={{
                color: '#6b7280',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Sign in to access your applications
              </p>
            </div>

            <form onSubmit={handleEmailContinue} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <label htmlFor="email" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '12px',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}>
                  Email address
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      width: '100%',
                      paddingLeft: '48px',
                      paddingRight: '16px',
                      paddingTop: '16px',
                      paddingBottom: '16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      fontSize: '16px',
                      background: 'white',
                      color: '#111827',
                      fontFamily: 'Lexend Deca, sans-serif',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter your email address"
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <Mail style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: '20px',
                    width: '20px',
                    color: '#9ca3af'
                  }} />
                </div>
              </div>

              {error && <ErrorMessage message={error} />}

              <PrimaryButton type="submit" loading={isLoading} loadingText="Checking...">
                Continue
                <ArrowRight style={{ marginLeft: '8px', height: '20px', width: '20px' }} />
              </PrimaryButton>
            </form>

            <div style={{
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
              }}>
                <Lock style={{
                  height: '16px',
                  width: '16px',
                  color: '#9ca3af',
                  marginRight: '8px',
                  flexShrink: 0
                }} />
                <p style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}>
                  Secure authentication
                </p>
              </div>
            </div>
          </div>
        </CardContainer>
        <FooterHelp />
      </PageWrapper>
    )
  }

  // ─── METHOD SELECTION STEP ─────────────────────────────
  if (step === 'METHOD_SELECTION') {
    return (
      <PageWrapper>
        <CardContainer>
          <GradientHeader
            icon={<img src="/logo.png" alt="Solharbor Logo" style={{ height: '40px', width: '40px', objectFit: 'contain' }} />}
            title="Solharbor"
            subtitle="Client Portal"
          />
          <div style={{ padding: '32px' }}>
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '24px'
              }}>
                <div>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    fontFamily: 'Lexend Deca, sans-serif',
                    marginBottom: '4px'
                  }}>
                    Signing in as
                  </p>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    fontFamily: 'Lexend Deca, sans-serif'
                  }}>
                    {email}
                  </p>
                </div>
                <button
                  onClick={() => goBack('EMAIL_INPUT')}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#2563eb',
                    fontSize: '14px',
                    fontFamily: 'Lexend Deca, sans-serif',
                    cursor: 'pointer',
                    fontWeight: '500',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#eff6ff' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                >
                  Change
                </button>
              </div>

              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#1f2937',
                marginBottom: '16px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Choose sign-in method
              </h2>

              {error && <div style={{ marginBottom: '16px' }}><ErrorMessage message={error} /></div>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {hasPassword && (
                <MethodButton
                  icon={<Key style={{ height: '24px', width: '24px', color: '#2563eb' }} />}
                  label="Sign in with Password"
                  subtitle="Use your account password"
                  onClick={() => { setError(''); setStep('PASSWORD_INPUT') }}
                  disabled={isLoading}
                />
              )}

              {hasPhone && (
                <MethodButton
                  icon={<Smartphone style={{ height: '24px', width: '24px', color: '#2563eb' }} />}
                  label="Sign in with SMS Code"
                  subtitle={`Send code to ${maskedPhone}`}
                  onClick={handleSelectSmsOtp}
                  disabled={isLoading}
                />
              )}

              <MethodButton
                icon={<Mail style={{ height: '24px', width: '24px', color: '#2563eb' }} />}
                label="Sign in with Email Link"
                subtitle="We'll send a secure link to your email"
                onClick={handleMagicLink}
                disabled={isLoading}
              />

              {!hasPassword && (
                <MethodButton
                  icon={<Lock style={{ height: '24px', width: '24px', color: '#2563eb' }} />}
                  label="Set Up a Password"
                  subtitle="We'll send a link to create your password"
                  onClick={handleSetupPassword}
                  disabled={isLoading}
                />
              )}
            </div>
          </div>
        </CardContainer>
        <FooterHelp />
      </PageWrapper>
    )
  }

  // ─── PASSWORD INPUT STEP ───────────────────────────────
  if (step === 'PASSWORD_INPUT') {
    return (
      <PageWrapper>
        <CardContainer>
          <GradientHeader
            icon={<Key style={{ height: '32px', width: '32px', color: 'white' }} />}
            title="Password"
            subtitle={email}
          />
          <div style={{ padding: '32px' }}>
            <div style={{ marginBottom: '24px' }}>
              <BackButton onClick={() => goBack('METHOD_SELECTION')} label="Other sign-in methods" />
            </div>

            <form onSubmit={handlePasswordSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label htmlFor="password" style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#374151',
                    fontFamily: 'Lexend Deca, sans-serif'
                  }}>
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => { setError(''); setStep('RESET_PASSWORD') }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#2563eb',
                      fontSize: '14px',
                      fontFamily: 'Lexend Deca, sans-serif',
                      cursor: 'pointer',
                      padding: 0,
                      transition: 'color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#1d4ed8';
                      e.currentTarget.style.textDecoration = 'underline';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#2563eb';
                      e.currentTarget.style.textDecoration = 'none';
                    }}
                  >
                    Forgot password?
                  </button>
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={{
                      width: '100%',
                      paddingLeft: '48px',
                      paddingRight: '16px',
                      paddingTop: '16px',
                      paddingBottom: '16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      fontSize: '16px',
                      background: 'white',
                      color: '#111827',
                      fontFamily: 'Lexend Deca, sans-serif',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter your password"
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <Lock style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: '20px',
                    width: '20px',
                    color: '#9ca3af'
                  }} />
                </div>
              </div>

              {error && <ErrorMessage message={error} />}

              <PrimaryButton type="submit" loading={isLoading} loadingText="Signing in...">
                Sign In
                <ArrowRight style={{ marginLeft: '8px', height: '20px', width: '20px' }} />
              </PrimaryButton>
            </form>
          </div>
        </CardContainer>
        <FooterHelp />
      </PageWrapper>
    )
  }

  // ─── SMS OTP INPUT STEP ────────────────────────────────
  if (step === 'SMS_OTP_INPUT') {
    return (
      <PageWrapper>
        <CardContainer>
          <GradientHeader
            icon={<Smartphone style={{ height: '32px', width: '32px', color: 'white' }} />}
            title="SMS Verification"
            subtitle={`Code sent to ${maskedPhone}`}
          />
          <div style={{ padding: '32px' }}>
            <div style={{ marginBottom: '24px' }}>
              <BackButton onClick={() => goBack('METHOD_SELECTION')} label="Other sign-in methods" />
            </div>

            <form onSubmit={handleVerifyOtp} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <label htmlFor="otp-code" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '12px',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}>
                  Enter 6-digit code
                </label>
                <input
                  id="otp-code"
                  name="otp-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  required
                  autoComplete="one-time-code"
                  value={otpCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '')
                    setOtpCode(val)
                  }}
                  style={{
                    width: '100%',
                    padding: '16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    fontSize: '28px',
                    fontWeight: '600',
                    background: 'white',
                    color: '#111827',
                    fontFamily: 'Lexend Deca, sans-serif',
                    outline: 'none',
                    transition: 'all 0.2s',
                    textAlign: 'center',
                    letterSpacing: '0.5em',
                    boxSizing: 'border-box'
                  }}
                  placeholder="000000"
                  onFocus={(e) => {
                    e.target.style.borderColor = '#3b82f6';
                    e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d1d5db';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {error && <ErrorMessage message={error} />}

              <PrimaryButton
                type="submit"
                loading={isLoading}
                loadingText="Verifying..."
                disabled={otpCode.length !== 6}
              >
                Verify Code
                <ArrowRight style={{ marginLeft: '8px', height: '20px', width: '20px' }} />
              </PrimaryButton>

              <div style={{ textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={handleSendSmsOtp}
                  disabled={resendCooldown > 0 || isLoading}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: resendCooldown > 0 ? '#9ca3af' : '#2563eb',
                    fontSize: '14px',
                    fontFamily: 'Lexend Deca, sans-serif',
                    cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer',
                    padding: '8px',
                    transition: 'color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (resendCooldown <= 0 && !isLoading) {
                      e.currentTarget.style.color = '#1d4ed8'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (resendCooldown <= 0 && !isLoading) {
                      e.currentTarget.style.color = '#2563eb'
                    }
                  }}
                >
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          </div>
        </CardContainer>
        <FooterHelp />
      </PageWrapper>
    )
  }

  // ─── MAGIC LINK SENT STEP ─────────────────────────────
  if (step === 'MAGIC_LINK_SENT') {
    return (
      <PageWrapper>
        <CardContainer>
          <GradientHeader
            icon={<CheckCircle style={{ height: '32px', width: '32px', color: 'white' }} />}
            title="Check your email"
            subtitle="We've sent you a secure login link"
          />
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <div style={{ marginBottom: '24px' }}>
              <p style={{
                color: '#6b7280',
                marginBottom: '16px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                We've sent a secure link to:
              </p>
              <div style={{
                background: '#f9fafb',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #e5e7eb'
              }}>
                <p style={{
                  fontWeight: '600',
                  color: '#2563eb',
                  wordBreak: 'break-all',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}>
                  {email}
                </p>
              </div>
            </div>

            <div style={{
              background: '#eff6ff',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              border: '1px solid #dbeafe'
            }}>
              <p style={{
                fontSize: '14px',
                color: '#1d4ed8',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                Click the link in your email to continue to your dashboard
              </p>
            </div>

            <button
              onClick={() => {
                setEmail('')
                setStep('EMAIL_INPUT')
                setError('')
              }}
              style={{
                width: '100%',
                padding: '16px 24px',
                background: 'white',
                color: '#2563eb',
                fontWeight: '600',
                borderRadius: '12px',
                border: '2px solid #2563eb',
                cursor: 'pointer',
                fontSize: '16px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#2563eb';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#2563eb';
              }}
            >
              Try a different email
            </button>
          </div>
        </CardContainer>
        <FooterHelp />
      </PageWrapper>
    )
  }

  // ─── RESET PASSWORD STEP ──────────────────────────────
  if (step === 'RESET_PASSWORD') {
    return (
      <PageWrapper>
        <CardContainer>
          <GradientHeader
            icon={<Lock style={{ height: '32px', width: '32px', color: 'white' }} />}
            title="Reset Password"
            subtitle="Enter your email to receive reset instructions"
          />
          <div style={{ padding: '32px' }}>
            <form onSubmit={handleResetPassword} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              <div>
                <label htmlFor="reset-email" style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  marginBottom: '12px',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}>
                  Email address
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="reset-email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      width: '100%',
                      paddingLeft: '48px',
                      paddingRight: '16px',
                      paddingTop: '16px',
                      paddingBottom: '16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      fontSize: '16px',
                      background: 'white',
                      color: '#111827',
                      fontFamily: 'Lexend Deca, sans-serif',
                      outline: 'none',
                      transition: 'all 0.2s',
                      boxSizing: 'border-box'
                    }}
                    placeholder="Enter your email address"
                    onFocus={(e) => {
                      e.target.style.borderColor = '#3b82f6';
                      e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d1d5db';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  <Mail style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    height: '20px',
                    width: '20px',
                    color: '#9ca3af'
                  }} />
                </div>
              </div>

              {error && <ErrorMessage message={error} />}

              <PrimaryButton type="submit" loading={isLoading} loadingText="Sending reset email...">
                Send Reset Email
                <ArrowRight style={{ marginLeft: '8px', height: '20px', width: '20px' }} />
              </PrimaryButton>

              <button
                type="button"
                onClick={() => goBack('PASSWORD_INPUT')}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  background: 'white',
                  color: '#6b7280',
                  fontWeight: '600',
                  borderRadius: '12px',
                  border: '1px solid #d1d5db',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontFamily: 'Lexend Deca, sans-serif',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#9ca3af';
                  e.currentTarget.style.color = '#374151';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                Back to sign in
              </button>
            </form>
          </div>
        </CardContainer>
        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            Need help? Contact admin@solharbor.com
          </p>
        </div>
      </PageWrapper>
    )
  }

  // ─── RESET EMAIL SENT STEP ────────────────────────────
  if (step === 'RESET_EMAIL_SENT') {
    return (
      <PageWrapper>
        <CardContainer>
          <GradientHeader
            icon={<CheckCircle style={{ height: '32px', width: '32px', color: 'white' }} />}
            title={isSetupPassword ? "Setup email sent" : "Reset email sent"}
            subtitle="Check your inbox for instructions"
          />
          <div style={{ padding: '32px', textAlign: 'center' }}>
            <div style={{ marginBottom: '24px' }}>
              <p style={{
                color: '#6b7280',
                marginBottom: '16px',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                {isSetupPassword
                  ? "We've sent password setup instructions to:"
                  : "We've sent password reset instructions to:"}
              </p>
              <div style={{
                background: '#f9fafb',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid #e5e7eb'
              }}>
                <p style={{
                  fontWeight: '600',
                  color: '#2563eb',
                  wordBreak: 'break-all',
                  fontFamily: 'Lexend Deca, sans-serif'
                }}>
                  {email}
                </p>
              </div>
            </div>

            <div style={{
              background: '#eff6ff',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              border: '1px solid #dbeafe'
            }}>
              <p style={{
                fontSize: '14px',
                color: '#1d4ed8',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                {isSetupPassword
                  ? "Click the link in your email to set up your password"
                  : "Click the link in your email to reset your password"}
              </p>
            </div>

            <button
              onClick={() => {
                setEmail('')
                setPassword('')
                setIsSetupPassword(false)
                setStep('EMAIL_INPUT')
                setError('')
              }}
              style={{
                width: '100%',
                padding: '16px 24px',
                background: 'white',
                color: '#2563eb',
                fontWeight: '600',
                borderRadius: '12px',
                border: '2px solid #2563eb',
                cursor: 'pointer',
                fontSize: '16px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#2563eb';
                e.currentTarget.style.color = 'white';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'white';
                e.currentTarget.style.color = '#2563eb';
              }}
            >
              Back to sign in
            </button>
          </div>
        </CardContainer>
        <FooterHelp />
      </PageWrapper>
    )
  }

  // Fallback
  return null
}
